'use client';

import { TableCell, TableRow, Typography, Link } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import StatusChip from 'components/status-chip';
import { useLocale } from 'hooks';
import { dateTimeToString, localizeFund, TranslatedText } from 'lib/utils';
import get from 'lodash/get';
import { STATUS_LABELS } from 'models/giving';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import thousands from 'thousands';

function GivingInfoBox({ giving, onViewSubscription }) {
  const status = get(giving, 'status');
  const statusLabel = typeof status === 'number' ? STATUS_LABELS[status] : status;
  const locale = useLocale();

  const getFundName = useCallback(() => {
    return localizeFund(get(giving, 'fund'), locale);
  }, [giving, locale]);

  const getGivingType = useCallback(() => {
    const _paymentMethod = get(giving, 'onlinePayment.paymentMethod')
      || get(giving, `paymentMethod.${locale === 'zh-Hant' ? 'nameChi' : locale === 'zh-Hans' ? 'nameChi' : 'nameEng'}`);
    return (
      <>
        <Typography variant='paragraph' color='secondary'><TranslatedText value={giving.type} /></Typography>
        {
          giving.subscription?.refId && (
            <div>
              <Typography variant='paragraph' color='info' sx={{ display: 'inline' }}>
                <TranslatedText value='Subscription Ref ID: ' />
              </Typography>
              <Link
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onViewSubscription && giving.subscriptionId) {
                    onViewSubscription(giving.subscriptionId);
                  }
                }}
                sx={{
                  fontFamily: 'monospace',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {giving.subscription?.refId}
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="xs" />
              </Link>
            </div>
          )
        }
        {_paymentMethod && (
          <Typography variant="subtitle1" color="textSecondary">
            {_paymentMethod}
          </Typography>
        )}
      </>
    );
  }, [giving, locale, onViewSubscription]);

  return (
    <TableRow sx={{ '&:hover': { backgroundColor: 'grey.50' } }}>
      <TableCell>
        <Typography variant='body2' color='info.700'>
          {dateTimeToString(get(giving, 'date'))}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant='body2' color='info.700'>
          HKD {thousands((get(giving, 'amount') || 0).toFixed(2))}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant='body2' color='info.700'>
          {getFundName()}
        </Typography>
      </TableCell>
      <TableCell>
        {getGivingType()}
      </TableCell>
      <TableCell>
        {statusLabel && <StatusChip status={statusLabel} />}
      </TableCell>
      <TableCell>
        <Typography variant='body2' color='info.700'>
          {get(giving, 'refId')}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

GivingInfoBox.propTypes = {
  giving: PropTypes.object.isRequired,
  onViewSubscription: PropTypes.func,
};

export default GivingInfoBox;
